import { Mark, mergeAttributes } from '@tiptap/core'

// Addition mark: ==text==
export const Addition = Mark.create({
  name: 'addition',
  priority: 1000,  // High priority to parse before other marks

  parseHTML() {
    return [
      {
        tag: 'mark.annotation-addition',
        priority: 60,  // Higher than default 50
      },
      {
        tag: 'mark[class="annotation-addition"]',
        priority: 60,
      },
      {
        tag: 'ins',
        priority: 60,
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'mark',
      mergeAttributes(HTMLAttributes, {
        class: 'annotation-addition',
      }),
      0,
    ]
  },

  addCommands() {
    return {
      setAddition:
        () =>
        ({ commands }) => {
          return commands.setMark(this.name)
        },
      toggleAddition:
        () =>
        ({ commands }) => {
          return commands.toggleMark(this.name)
        },
      unsetAddition:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name)
        },
    }
  },
})

// Deletion mark: ~~text~~
export const Deletion = Mark.create({
  name: 'deletion',
  priority: 1000,  // High priority to parse before other marks

  parseHTML() {
    return [
      {
        tag: 'mark.annotation-deletion',
        priority: 60,  // Higher than default 50
      },
      {
        tag: 'mark[class="annotation-deletion"]',
        priority: 60,
      },
      {
        tag: 'del',
        priority: 60,
      },
      {
        tag: 's',
        priority: 60,
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'mark',
      mergeAttributes(HTMLAttributes, {
        class: 'annotation-deletion',
      }),
      0,
    ]
  },

  addCommands() {
    return {
      setDeletion:
        () =>
        ({ commands }) => {
          return commands.setMark(this.name)
        },
      toggleDeletion:
        () =>
        ({ commands }) => {
          return commands.toggleMark(this.name)
        },
      unsetDeletion:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name)
        },
    }
  },
})

// Note mark: <!-- NOTE: text -->
export const Note = Mark.create({
  name: 'note',
  priority: 1000,  // High priority to parse before other marks

  parseHTML() {
    return [
      {
        tag: 'span.annotation-note',
        priority: 60,  // Higher than default 50
      },
      {
        tag: 'span[class="annotation-note"]',
        priority: 60,
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'annotation-note',
      }),
      0,
    ]
  },
})

// Flash mark: {{flash:id}}text{{/flash}} - temporary highlight for real-time edits
export const Flash = Mark.create({
  name: 'flash',
  priority: 1001,  // Highest priority - parse before other marks

  addAttributes() {
    return {
      flashId: {
        default: null,
        parseHTML: element => element.getAttribute('data-flash-id'),
        renderHTML: attributes => {
          if (!attributes.flashId) return {}
          return { 'data-flash-id': attributes.flashId }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'mark.annotation-flash',
        priority: 70,  // Higher than other annotations
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'mark',
      mergeAttributes(HTMLAttributes, {
        class: 'annotation-flash',
      }),
      0,
    ]
  },
})

// Helper to parse annotation markers from markdown
export function parseAnnotations(content: string): string {
  // Convert {{flash:id}}text{{/flash}} to <mark class="annotation-flash">
  // Must be parsed FIRST before other annotations to avoid conflicts
  let parsed = content.replace(
    /\{\{flash:([a-z0-9]+)\}\}([\s\S]*?)\{\{\/flash\}\}/g,
    '<mark class="annotation-flash" data-flash-id="$1">$2</mark>'
  )

  // Convert <ins>additions</ins> to <mark class="annotation-addition">
  // (backend sends <ins>/<del> tags for AI changes)
  parsed = parsed.replace(
    /<ins>([\s\S]*?)<\/ins>/g,
    '<mark class="annotation-addition">$1</mark>'
  )

  // Convert <del>deletions</del> to <mark class="annotation-deletion">
  parsed = parsed.replace(
    /<del>([\s\S]*?)<\/del>/g,
    '<mark class="annotation-deletion">$1</mark>'
  )

  // Convert ==additions== to <mark class="annotation-addition">
  parsed = parsed.replace(
    /==([\s\S]+?)==/g,
    '<mark class="annotation-addition">$1</mark>'
  )

  // Convert ~~deletions~~ to <mark class="annotation-deletion">
  parsed = parsed.replace(
    /~~([\s\S]+?)~~/g,
    '<mark class="annotation-deletion">$1</mark>'
  )

  // Convert <!-- NOTE: text --> to styled note
  parsed = parsed.replace(
    /<!--\s*NOTE:\s*([\s\S]+?)\s*-->/g,
    '<span class="annotation-note">Note: $1</span>'
  )

  return parsed
}

// Strip flash markers from content (call after animation completes)
export function stripFlashMarkers(content: string): string {
  if (!content) return ''
  // Remove {{flash:id}} and {{/flash}} markers, keeping the text inside
  return content.replace(/\{\{flash:[a-z0-9]+\}\}([\s\S]*?)\{\{\/flash\}\}/g, '$1')
}

// Check if content has any flash markers
export function hasFlashMarkers(content: string): boolean {
  if (!content) return false
  return /\{\{flash:[a-z0-9]+\}\}/.test(content)
}

// Helper to convert HTML back to annotation markers
export function serializeAnnotations(html: string): string {
  // Convert addition marks back to ==text==
  // Use [\s\S]*? for non-greedy match that handles any content including newlines
  let serialized = html.replace(
    /<mark[^>]*class="[^"]*annotation-addition[^"]*"[^>]*>([\s\S]*?)<\/mark>/g,
    '==$1=='
  )

  // Convert deletion marks back to ~~text~~
  serialized = serialized.replace(
    /<mark[^>]*class="[^"]*annotation-deletion[^"]*"[^>]*>([\s\S]*?)<\/mark>/g,
    '~~$1~~'
  )

  // Convert note spans back to <!-- NOTE: -->
  serialized = serialized.replace(
    /<span[^>]*class="[^"]*annotation-note[^"]*"[^>]*>(?:Note:\s*)?([\s\S]*?)<\/span>/g,
    '<!-- NOTE: $1 -->'
  )

  return serialized
}

// Check if content has any annotation markers (additions, deletions, or notes)
export function hasAnnotations(content: string): boolean {
  if (!content) return false
  return (
    /==([\s\S]+?)==/.test(content) ||
    /~~([\s\S]+?)~~/.test(content) ||
    /<!--\s*NOTE:[\s\S]*?-->/.test(content) ||
    /<ins>[\s\S]*?<\/ins>/.test(content) ||
    /<del>[\s\S]*?<\/del>/.test(content)
  )
}

// Accept annotations: additions become permanent text, deletions are removed
// Called after a turn completes to "accept" all pending changes
export function acceptAnnotations(content: string): string {
  if (!content) return ''

  // Remove NOTE comments (use [\s\S] instead of 's' flag for ES5 compat)
  let accepted = content.replace(/<!--\s*NOTE:[\s\S]*?-->/g, '')

  // Accept additions: ==text== → text (keep the text, remove markers)
  accepted = accepted.replace(/==([\s\S]*?)==/g, '$1')

  // Accept <ins>text</ins> → text (keep the text, remove markers)
  accepted = accepted.replace(/<ins>([\s\S]*?)<\/ins>/g, '$1')

  // Accept deletions: ~~text~~ → removed entirely
  accepted = accepted.replace(/~~[\s\S]*?~~/g, '')

  // Accept <del>text</del> → removed entirely
  accepted = accepted.replace(/<del>[\s\S]*?<\/del>/g, '')

  // Clean up empty list items (bullet points with no content after deletion)
  // This handles orphaned list prefixes that remain when deletions are stripped
  // E.g., "1. ~~deleted text~~" becomes "1. " after stripping, which needs cleanup
  //
  // Matches (with optional leading whitespace for nested lists):
  // - Unordered: "- ", "* ", "+ "
  // - Numbered: "1. ", "2. ", "10. ", etc.
  // - Letter: "a. ", "b. ", "A. ", etc.
  // - Roman numerals: "i. ", "ii. ", "iv. ", "I. ", etc.
  accepted = accepted.replace(/^[ \t]*[\-\*\+]\s*$/gm, '')
  accepted = accepted.replace(/^[ \t]*\d+\.\s*$/gm, '')
  accepted = accepted.replace(/^[ \t]*[a-zA-Z]+\.\s*$/gm, '')
  accepted = accepted.replace(/^[ \t]*[ivxIVX]+\.\s*$/gm, '')

  // Clean up empty headings
  accepted = accepted.replace(/^#{1,6}\s*$/gm, '')

  // Clean up any double spaces or blank lines left behind
  accepted = accepted.replace(/\n{3,}/g, '\n\n')
  accepted = accepted.replace(/  +/g, ' ')

  // Trim trailing whitespace from lines
  accepted = accepted.replace(/[ \t]+$/gm, '')

  return accepted.trim()
}

/**
 * Accept a single annotation by its text content.
 * - For additions: keeps the text, removes == markers
 * - For deletions: removes the text AND ~~ markers
 * - For notes: removes the entire note
 */
export function acceptSingleAnnotation(
  content: string,
  annotationText: string,
  type: 'addition' | 'deletion' | 'note'
): string {
  if (!content || !annotationText) return content

  // Escape special regex characters in the annotation text
  const escapedText = annotationText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  switch (type) {
    case 'addition':
      // Accept addition: ==text== → text (keep text, remove markers)
      // Match the exact annotation text wrapped in ==
      return content.replace(new RegExp(`==${escapedText}==`, 'g'), annotationText)

    case 'deletion':
      // Accept deletion: ~~text~~ → removed entirely
      return cleanupOrphanedMarkdown(
        content.replace(new RegExp(`~~${escapedText}~~`, 'g'), '')
      )

    case 'note':
      // Accept note: remove the entire <!-- NOTE: text --> comment
      // The annotationText here is the content inside the note (without "Note: " prefix)
      const noteText = annotationText.replace(/^Note:\s*/i, '')
      const escapedNoteText = noteText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return content.replace(
        new RegExp(`<!--\\s*NOTE:\\s*${escapedNoteText}\\s*-->`, 'g'),
        ''
      )
  }
}

/**
 * Reject a single annotation by its text content.
 * - For additions: removes the text AND == markers
 * - For deletions: keeps the text, removes ~~ markers
 * - For notes: keeps the note text as regular text
 */
export function rejectSingleAnnotation(
  content: string,
  annotationText: string,
  type: 'addition' | 'deletion' | 'note'
): string {
  if (!content || !annotationText) return content

  // Escape special regex characters in the annotation text
  const escapedText = annotationText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  switch (type) {
    case 'addition':
      // Reject addition: ==text== → removed entirely
      return cleanupOrphanedMarkdown(
        content.replace(new RegExp(`==${escapedText}==`, 'g'), '')
      )

    case 'deletion':
      // Reject deletion: ~~text~~ → text (keep the text, remove markers)
      return content.replace(new RegExp(`~~${escapedText}~~`, 'g'), annotationText)

    case 'note':
      // Reject note: <!-- NOTE: text --> → text (keep the note content as regular text)
      const noteText = annotationText.replace(/^Note:\s*/i, '')
      const escapedNoteText = noteText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return content.replace(
        new RegExp(`<!--\\s*NOTE:\\s*${escapedNoteText}\\s*-->`, 'g'),
        noteText
      )
  }
}

/**
 * Clean up orphaned markdown elements after annotation removal.
 */
function cleanupOrphanedMarkdown(content: string): string {
  let cleaned = content

  // Clean up empty list items
  cleaned = cleaned.replace(/^[\-\*\+]\s*$/gm, '')
  cleaned = cleaned.replace(/^\d+\.\s*$/gm, '')

  // Clean up empty headings
  cleaned = cleaned.replace(/^#{1,6}\s*$/gm, '')

  // Clean up extra blank lines
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n')

  // Trim trailing whitespace from lines
  cleaned = cleaned.replace(/[ \t]+$/gm, '')

  return cleaned
}

// Declare module augmentation for TypeScript
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    addition: {
      setAddition: () => ReturnType
      toggleAddition: () => ReturnType
      unsetAddition: () => ReturnType
    }
    deletion: {
      setDeletion: () => ReturnType
      toggleDeletion: () => ReturnType
      unsetDeletion: () => ReturnType
    }
  }
}
